import { useEffect, useMemo, useState } from "react";
import { Users, Search, Shield, Ban, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { TIMEOUT_OPTIONS, timeoutUser, banUser } from "@/components/ChatModeration";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

function StatusBadge({ u }) {
  if (u.role === "admin") return <span className="inline-flex items-center gap-1 text-crimson-bright"><Shield size={12} /> Admin</span>;
  if (u.banned) return <span className="inline-flex items-center gap-1 text-crimson-bright" data-testid={`user-status-${u.user_id}`}><Ban size={12} /> Banned</span>;
  if (u.muted_until) return <span className="inline-flex items-center gap-1 text-amber-400" data-testid={`user-status-${u.user_id}`}><Clock size={12} /> Timed out until {fmt(u.muted_until)}</span>;
  return <span className="text-zinc-400" data-testid={`user-status-${u.user_id}`}>Active</span>;
}

function UserActions({ u, onChange }) {
  if (u.role === "admin") return null;
  const name = u.username || u.name;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        defaultValue=""
        onChange={async (e) => { const m = Number(e.target.value); e.target.value = ""; const r = await timeoutUser(u.user_id, m, name); if (r) onChange(r); }}
        className="tavern-input px-2 py-1.5 rounded-sm text-xs"
        data-testid={`user-timeout-${u.user_id}`}
      >
        <option value="" disabled>Timeout…</option>
        {TIMEOUT_OPTIONS.map((o) => <option key={o.minutes} value={o.minutes}>{o.label}</option>)}
        {u.muted_until && <option value="0">Clear timeout</option>}
      </select>
      <button
        onClick={async () => { const r = await banUser(u.user_id, !u.banned, name); if (r) onChange(r); }}
        data-testid={`user-ban-${u.user_id}`}
        className={`${u.banned ? "btn-ghost" : "btn-crimson"} px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase`}
      >
        {u.banned ? "Unban" : "Ban"}
      </button>
    </div>
  );
}

export function UsersSection() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => setUsers([])); }, []);

  const update = (patch) => setUsers((list) => list.map((u) => (u.user_id === patch.user_id ? { ...u, ...patch } : u)));

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!users) return [];
    return s ? users.filter((u) => [u.username, u.name, u.email].some((v) => v?.toLowerCase().includes(s))) : users;
  }, [users, q]);

  return (
    <section className="tavern-card p-6 sm:p-8" data-testid="admin-users-section">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-crimson-bright" />
          <h2 className="font-display tracking-[0.3em] uppercase text-sm text-white">Members {users && `(${users.length})`}</h2>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search username, name or email"
            className="tavern-input pl-9 pr-3 py-2 rounded-sm text-sm w-64 max-w-full" data-testid="admin-users-search" />
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Only admins can see this list. Timed-out members can read chat but not post. Banned members can't open chat, and their messages are removed.</p>
      {users === null ? (
        <div className="text-xs tracking-widest uppercase text-zinc-500 animate-pulse">Loading members...</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-zinc-500">No members found.</div>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm min-w-[760px]" data-testid="admin-users-table">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.25em] uppercase text-zinc-500 border-b border-zinc-800">
                <th className="px-2 py-2 font-medium">Username</th>
                <th className="px-2 py-2 font-medium">Name / Email</th>
                <th className="px-2 py-2 font-medium">Joined</th>
                <th className="px-2 py-2 font-medium">Msgs 24h</th>
                <th className="px-2 py-2 font-medium">Status</th>
                <th className="px-2 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.user_id} className="border-b border-zinc-900 align-middle" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="px-2 py-3 text-white font-semibold">{u.username || <span className="text-zinc-600 font-normal italic">not set</span>}</td>
                  <td className="px-2 py-3">
                    <div className="text-zinc-200">{u.name}</div>
                    <div className="text-xs text-zinc-500">{u.email} · {u.auth_provider === "google" ? "Google" : "Email"}</div>
                  </td>
                  <td className="px-2 py-3 text-xs text-zinc-400">{fmt(u.created_at)}</td>
                  <td className="px-2 py-3 text-zinc-300">{u.messages_24h}</td>
                  <td className="px-2 py-3 text-xs"><StatusBadge u={u} /></td>
                  <td className="px-2 py-3"><UserActions u={u} onChange={update} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
